import { CancelOutlined, ContentCopy } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useMemo } from 'react';
import { DeleteTicketFileByIdDataWrapper } from 'src/app/components/data-wrappers/tickets/DeleteTicketFileByIdDataWrapper';
import TicketFile from 'src/data/models/common/ticketFile';
import { useGenericStyles } from 'src/shared/styles/genericStyles';
import Table from 'src/view/components/table/table/Table';
import { NewTableColumn, TABLE_CELL_HEIGHT } from 'src/view/components/table/table/Types';

const useStyles = makeStyles(() => ({
    fileRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        gap: 8,
    },
    linkContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flex: 1,
        textWrap: 'nowrap',
    },
    copyLinkButton: {
        display: 'flex',
        alignItems: 'center',
        borderRight: '1px solid',
        paddingRight: '7px',
    },
    copyLinkIcon: {
        fontSize: '1rem',
        marginRight: '2px',
    },
}));

export interface TicketUploadedFilesTableProps {
    ticketId: string;
    files?: TicketFile[];
    loading: boolean;
    onFileDeleted?: () => void;
    onClickTicketFile?: (ticketFileId: string) => Promise<void>;
    onClickCopyTicketLink?: (ticketFileId: string) => Promise<void>;
}

export default function TicketUploadedFilesTable({
    files,
    loading,
    onFileDeleted,
    onClickTicketFile,
    onClickCopyTicketLink,
}: TicketUploadedFilesTableProps): JSX.Element {
    const classes = useStyles();
    const genericClasses = useGenericStyles();

    const cols = useMemo<NewTableColumn<TicketFile>[]>(
        () => [
            {
                key: 'id',
                title: 'Name',
                cellRenderer: (rowData) => {
                    const isTicketLink = !rowData.file;

                    return (
                        <div className={classes.fileRow}>
                            <div className={classes.linkContainer}>
                                {isTicketLink && (
                                    <span
                                        className={`${genericClasses.link} ${classes.copyLinkButton}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClickCopyTicketLink?.(rowData.id);
                                        }}
                                    >
                                        <ContentCopy
                                            className={`${genericClasses.link} ${classes.copyLinkIcon}`}
                                        />
                                        Copy link
                                    </span>
                                )}

                                <span
                                    className={genericClasses.link}
                                    onClick={() => onClickTicketFile?.(rowData.id)}
                                >
                                    {rowData.file?.name || 'Open ticket link'}
                                </span>
                            </div>

                            <DeleteTicketFileByIdDataWrapper onSuccess={onFileDeleted}>
                                {({ deleteTicketFile, loading }) => (
                                    <>
                                        {loading ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <CancelOutlined
                                                className={genericClasses.clickable}
                                                color="disabled"
                                                onClick={() => deleteTicketFile(rowData.id)}
                                                fontSize="small"
                                            />
                                        )}
                                    </>
                                )}
                            </DeleteTicketFileByIdDataWrapper>
                        </div>
                    );
                },
            },
        ],
        [classes, genericClasses, onClickCopyTicketLink, onClickTicketFile, onFileDeleted]
    );

    return (
        <Table<TicketFile>
            data={files}
            rowIdResolver={({ id }) => ({ id })}
            columns={cols}
            loading={loading}
            maxWidth={600}
            rowHeight={TABLE_CELL_HEIGHT.NONE}
        />
    );
}
